import { useState, useEffect, useCallback, memo } from "react";
import Swal from "sweetalert2";
import AnimacionCarga from "../animacion_de_carga";
import BarraSuperior from "../barra_superior";
import BotonSOS from "../sos/boton_sos";
import { consultarSimple, actualizarRegistro } from "../../../functions/crud";
import "bootstrap-icons/font/bootstrap-icons.css";


const CAMPOS = [
  {
    id: "versionAndroid",
    ruta: "version/android",
    etiqueta: "Android",
    seccion: "Versiones App",
    multilinea: false,
  },
  {
    id: "versionIos",
    ruta: "version/ios",
    etiqueta: "iOS",
    seccion: "Versiones App",
    multilinea: false,
  },
  {
    id: "notificacionGlobal",
    ruta: "coordi_ph_clientes/notificacionGlobal/mensaje",
    etiqueta: "Notificación global",
    seccion: "Notificaciones globales",
    multilinea: true,
  },
];

const SECCIONES = ["Versiones App", "Notificaciones globales"];

const esSuper = () => localStorage.getItem("Permisos") === "SUPER";

const leerValor = async (ruta) => {
  const valor = await consultarSimple(ruta);
  if (valor === null || valor === undefined) return "";
  if (typeof valor === "object") return Object.keys(valor).length ? JSON.stringify(valor) : "";
  return String(valor);
};

const ConfiguracionesGlobales = memo(() => {
  const [valores, setValores] = useState({});
  const [originales, setOriginales] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const capturarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const leidos = await Promise.all(CAMPOS.map((campo) => leerValor(campo.ruta)));
      const mapa = {};
      CAMPOS.forEach((campo, indice) => { mapa[campo.id] = leidos[indice]; });
      setValores(mapa);
      setOriginales(mapa);
    } catch (error) {
      console.error("Error al cargar configuraciones globales:", error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron cargar las configuraciones." });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  const cambiados = CAMPOS.filter((campo) => valores[campo.id] !== originales[campo.id]);

  const guardar = useCallback(async () => {
    const resumen = cambiados.length
      ? cambiados.map((campo) => `<li><strong>${campo.etiqueta}</strong></li>`).join("")
      : "";

    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "¿Guardar cambios globales?",
      html: `
        <p style="text-align:left;font-size:14px;color:#555;">
          Estos valores afectan a <strong>todas las copropiedades</strong> y a las apps móviles.
        </p>
        ${resumen ? `<ul style="text-align:left;font-size:14px;color:#2c3e50;">${resumen}</ul>` : ""}
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#c0392b",
    });

    if (!isConfirmed) return;

    setGuardando(true);
    try {
      await Promise.all(
        cambiados.map((campo) => actualizarRegistro(campo.ruta, valores[campo.id]))
      );

      setOriginales({ ...valores });
      Swal.fire({
        icon: "success",
        title: "Datos actualizados exitosamente",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error al guardar configuraciones globales:", error);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudieron guardar los cambios." });
    } finally {
      setGuardando(false);
    }
  }, [cambiados, valores]);

  if (!esSuper()) {
    return (
      <div style={s.page}>
        <BarraSuperior />
        <div style={s.sinAcceso}>
          <i className="bi bi-shield-lock" style={{ fontSize: 34, color: "#d1d5db" }} />
          <p style={{ marginTop: 12 }}>Esta sección está disponible únicamente para usuarios SUPER.</p>
        </div>
      </div>
    );
  }

  if (cargando) return <AnimacionCarga />;

  const hayCambios = cambiados.length > 0;

  return (
    <div style={s.page}>
      <BarraSuperior />

      <div style={s.contenedor}>
        <header style={s.header}>
          <div>
            <h1 style={s.headerTitle}>Configuraciones globales</h1>
            <p style={s.headerSub}>
              Valores que aplican a todas las copropiedades y a las apps móviles.
            </p>
          </div>

          <div style={s.headerAcciones}>
            <span style={{ ...s.chip, ...(hayCambios ? s.chipPendiente : s.chipOk) }}>
              <i className={`bi ${hayCambios ? "bi-exclamation-circle" : "bi-check-circle"}`} />
              {hayCambios
                ? `${cambiados.length} cambio${cambiados.length > 1 ? "s" : ""} sin guardar`
                : "Sin cambios"}
            </span>

            <button
              type="button"
              style={{ ...s.botonGuardar, ...(guardando || !hayCambios ? s.botonDeshabilitado : {}) }}
              onClick={guardar}
              disabled={guardando || !hayCambios}
            >
              <i className="bi bi-save" style={{ marginRight: 8 }} />
              {guardando ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </header>

        <div style={s.tarjetas}>
          {SECCIONES.map((seccion) => {
            const campos = CAMPOS.filter((campo) => campo.seccion === seccion);
            const anchaCompleta = campos.some((campo) => campo.multilinea);

            return (
              <section
                key={seccion}
                style={{ ...s.tarjeta, ...(anchaCompleta ? s.tarjetaAncha : {}) }}
              >
                <h2 style={s.seccionTitulo}>{seccion}</h2>

                <div style={s.grupoCampos}>
                  {campos.map((campo) => {
                    const modificado = valores[campo.id] !== originales[campo.id];

                    return (
                      <div
                        key={campo.id}
                        style={{ ...s.campo, ...(campo.multilinea ? s.campoAncho : {}) }}
                      >
                        <label style={s.etiqueta} htmlFor={campo.id}>
                          {campo.etiqueta}
                          {modificado && <span style={s.punto} title="Modificado" />}
                        </label>

                        {campo.multilinea ? (
                          <textarea
                            id={campo.id}
                            style={{
                              ...s.entrada,
                              ...s.entradaLarga,
                              ...(modificado ? s.entradaModificada : {}),
                            }}
                            value={valores[campo.id] ?? ""}
                            onChange={(e) => setValores({ ...valores, [campo.id]: e.target.value })}
                          />
                        ) : (
                          <input
                            id={campo.id}
                            style={{ ...s.entrada, ...(modificado ? s.entradaModificada : {}) }}
                            value={valores[campo.id] ?? ""}
                            onChange={(e) => setValores({ ...valores, [campo.id]: e.target.value })}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>

      <BotonSOS />
    </div>
  );
});

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 100,
  },
  contenedor: { width: "100%", maxWidth: 1600, margin: "0 auto", padding: "0 32px", boxSizing: "border-box" },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    padding: "28px 0 20px",
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 24,
    minWidth: 0,
  },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e" },
  headerSub: { margin: "6px 0 0", fontSize: 13.5, color: "#6b7280" },
  headerAcciones: { display: "flex", alignItems: "center", gap: 12, flexShrink: 0 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 12.5,
    fontWeight: 600,
  },
  chipOk: { background: "#ecfdf5", color: "#047857" },
  chipPendiente: { background: "#fef3c7", color: "#92400e" },
  tarjetas: { display: "flex", flexDirection: "column", gap: 24 },
  tarjeta: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "22px 24px 8px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  tarjetaAncha: {},
  seccionTitulo: {
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1a2e",
    margin: "0 0 18px",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  grupoCampos: { display: "flex", flexWrap: "wrap", gap: 18 },
  campo: { flex: "1 1 240px", minWidth: 0, marginBottom: 16 },
  campoAncho: { flex: "1 1 100%" },
  etiqueta: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 6,
    color: "#6b7280",
    fontSize: 13,
    fontWeight: 500,
  },
  punto: { width: 7, height: 7, borderRadius: "50%", background: "#f59e0b" },
  entrada: {
    width: "100%",
    padding: "11px 13px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    color: "#1a1a2e",
    background: "#ffffff",
    boxSizing: "border-box",
    outline: "none",
    fontFamily: "inherit",
  },
  entradaModificada: { borderColor: "#f59e0b", background: "#fffbeb" },
  entradaLarga: { minHeight: 130, resize: "vertical", lineHeight: 1.5 },
  botonGuardar: {
    padding: "11px 24px",
    border: "none",
    borderRadius: 10,
    background: "#c0392b",
    color: "#ffffff",
    fontSize: 14.5,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  botonDeshabilitado: { background: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed" },
  sinAcceso: {
    maxWidth: 420,
    margin: "80px auto",
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 15,
  },
};

export default ConfiguracionesGlobales;
